import { PERMISSION_SCOPES } from '@clockwise/shared';
import { z } from 'zod';

export const PermissionEntrySchema = z.object({
  permission: z.string(),
  scope: z.enum(PERMISSION_SCOPES).optional(),
});
export type PermissionEntry = z.infer<typeof PermissionEntrySchema>;

export const SystemRoleSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  /** The Business Unit the role belongs to. */
  businessUnitId: z.string().uuid(),
  name: z.string(),
  description: z.string().nullable(),
  permissions: z.array(PermissionEntrySchema),
  active: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type SystemRole = z.infer<typeof SystemRoleSchema>;

const SystemRoleBody = z.object({
  name: z.string().min(1).max(120),
  description: z.string().nullable().optional(),
  permissions: z.array(PermissionEntrySchema).max(100).optional(),
  active: z.boolean().optional(),
});

export const CreateSystemRoleInput = SystemRoleBody.extend({
  /** The unit the role belongs to; optional while the tenant has one. */
  businessUnitId: z.string().uuid().optional(),
});
export type CreateSystemRoleInput = z.infer<typeof CreateSystemRoleInput>;

export const PatchSystemRoleInput = SystemRoleBody.partial();
export type PatchSystemRoleInput = z.infer<typeof PatchSystemRoleInput>;

/** POST /api/system-roles/:id/clone — anything left out is copied from the source. */
export const CloneSystemRoleInput = SystemRoleBody.omit({ active: true }).partial();
export type CloneSystemRoleInput = z.infer<typeof CloneSystemRoleInput>;

/** GET /api/system-roles/:id/clone — what a clone would create, before creating it. */
export const SystemRoleDraftSchema = z.object({
  name: z.string(),
  description: z.string().nullable(),
  permissions: z.array(PermissionEntrySchema),
  /** The source's Business Unit: the copy is made there. */
  businessUnitId: z.string().uuid(),
});
export type SystemRoleDraft = z.infer<typeof SystemRoleDraftSchema>;

export interface ListSystemRolesQuery {
  q?: string;
  /** The roles of one unit. */
  businessUnitId?: string;
  active?: boolean;
  sortField?: 'name' | 'createdAt' | 'updatedAt';
  sortDirection?: 'asc' | 'desc';
  limit?: number;
  offset?: number;
}
